import React, {useMemo, useRef, useState} from "react";
import CFAdminNotification from "../../components/CFAdminNotification";
import CFAdminConfirmDialog from "../../components/CFAdminConfirmDialog";
import VinUpdateService from "../../services/VinUpdateService";
import VinImportService from "../../services/VinImportService";
import "./CFAdminVinUpdate.scss";

type VinAttribute = {
    attributeCode: number;
    attributeId: number;
    attributeValue: string;
};

type AttributeValueOption = {
    attributeValueId: number;
    attributeValueDescription: string;
};

type VinFragmentDetail = {
    id: number;
    vinSigniPattrnMask: string;
    attributes: Record<string, VinAttribute>;
};

const ATTRIBUTE_ICONS: Record<string, string> = {
    Make: "\uD83C\uDFF7\uFE0F",
    Model: "\uD83D\uDE99",
    Year: "\uD83D\uDCC5",
    Series: "\uD83C\uDFAF",
    VehicleType: "\uD83D\uDE90",
    BodyStyle: "\uD83D\uDE97",
    DriveTrain: "\u2699\uFE0F",
    Transmission: "\uD83D\uDD27",
    EngineDisplacement: "\uD83D\uDEE2\uFE0F",
    Cylinders: "\uD83D\uDD29",
    Fuel: "\u26FD",
    EngineManufacturer: "\uD83C\uDFED",
    BrakesWeightRatingGVWR: "\u2696\uFE0F",
    BrakesPower: "\uD83D\uDED1",
    RestraintsDriver: "\uD83E\uDEA2",
    Cab: "\uD83D\uDE9A",
    AxleWheelbase: "\uD83D\uDCCF",
    AssemblyCountry: "\uD83C\uDF0E",
    AssemblyPlant: "\uD83C\uDFD7\uFE0F",
};

// Maps the response attribute name to the backend DTO field base name.
// The code column uses the base name, the value column uses `${base}Value`.
const ATTRIBUTE_FIELD_MAP: Record<string, string> = {
    Year: "year",
    Make: "make",
    Model: "model",
    Series: "series",
    Drivetrain: "driveTrain",
    Cylinders: "cylinders",
    Transmission: "transmission",
    "Engine Displacement": "engineDisplacement",
    VehicleType: "vehicleType",
    BodyStyle: "bodyStyle",
    Fuel: "fuel",
    WAxlesWheelbase: "axleWheelbase",
    EngineManufacturer: "engineManufacturer",
    AssemblyPlant: "assemblyPlant",
    AssemblyCountry: "assemblyCountry",
    BrakesPower: "brakesPower",
    BrakesWeightRatingGVWR: "brakesWeightRatingGVWR",
    RestraintsDriver: "restraintsDriver",
    Cab: "cab",
};

// Overrides the label shown in the UI for a given response attribute name.
const ATTRIBUTE_LABELS: Record<string, string> = {
    WAxlesWheelbase: "AxlesWheelbase",
};

type AttributeValidationRule = {
    maxLength?: number;
    numeric?: { precision: number; scale: number };
};

// Validation rules keyed by the response attribute name. These mirror the
// backend column definitions so values are rejected before submission.
const ATTRIBUTE_VALIDATION: Record<string, AttributeValidationRule> = {
    WAxlesWheelbase: {numeric: {precision: 6, scale: 1}},
    EngineManufacturer: {maxLength: 100},
    AssemblyPlant: {maxLength: 150},
    AssemblyCountry: {maxLength: 50},
    BrakesPower: {maxLength: 100},
    RestraintsDriver: {maxLength: 100},
    Cab: {maxLength: 100},
};

// Returns a warning message when the value violates its column rule, else "".
const validateAttributeValue = (name: string, value: string = ""): string => {
    const rule = ATTRIBUTE_VALIDATION[name];
    if (!rule) {
        return "";
    }
    const label = ATTRIBUTE_LABELS[name] ?? name;
    if (rule.maxLength !== undefined && value.length > rule.maxLength) {
        return `${label} must not exceed ${rule.maxLength} characters (currently ${value.length}).`;
    }
    if (rule.numeric && value.trim() !== "") {
        const {precision, scale} = rule.numeric;
        const wholeDigits = precision - scale;
        const numericPattern = new RegExp(String.raw`^\d{1,${wholeDigits}}(\.\d{1,${scale}})?$`);
        if (!numericPattern.test(value.trim())) {
            return `${label} must be numeric with up to ${wholeDigits} digit${wholeDigits === 1 ? "" : "s"} and ${scale} decimal place${scale === 1 ? "" : "s"}.`;
        }
    }
    return "";
};

function CFAdminVinUpdate() {
    const notificationRef = useRef<any>();
    const [step, setStep] = useState<1 | 2>(1);
    const [vinFragment, setVinFragment] = useState("");
    const [detail, setDetail] = useState<VinFragmentDetail | null>(null);
    const [values, setValues] = useState<Record<string, string>>({});
    const [optionsByAttributeId, setOptionsByAttributeId] = useState<Record<number, AttributeValueOption[]>>({});
    const [confirmOpen, setConfirmOpen] = useState(false);

    const attributeEntries = useMemo(
        () => (detail ? Object.entries(detail.attributes) : []),
        [detail]
    );

    const filledCount = attributeEntries.filter(
        ([name]) => (values[name] ?? "").trim() !== ""
    ).length;
    const totalCount = attributeEntries.length;
    const percent = totalCount === 0 ? 0 : Math.round((filledCount / totalCount) * 100);

    const hasChanges = useMemo(
        () =>
            attributeEntries.some(
                ([name, attribute]) => (values[name] ?? "") !== attribute.attributeValue
            ),
        [attributeEntries, values]
    );

    const validationErrors = useMemo(() => {
        const errors: Record<string, string> = {};
        attributeEntries.forEach(([name]) => {
            const message = validateAttributeValue(name, values[name] ?? "");
            if (message) {
                errors[name] = message;
            }
        });
        return errors;
    }, [attributeEntries, values]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    const showError = (message: string) => {
        notificationRef.current?.failureMessage(message);
    };

    const handleVinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setVinFragment(event.target.value.toUpperCase());
    };

    const handleNext = async () => {
        const fragment = vinFragment.trim();
        if (!/^[A-Z0-9]{8}\s[A-Z0-9]{2}$/.test(fragment)) {
            showError("Please enter a VIN pattern in the format '1FDYE1C8 TK' (8 characters, a space, then 2 characters).");
            return;
        }
        try {
            const response = await VinUpdateService.retrieveVinFragment(fragment);
            const data: VinFragmentDetail = response.data;
            const initialValues: Record<string, string> = {};
            Object.entries(data.attributes).forEach(([name, attribute]) => {
                initialValues[name] = attribute.attributeValue;
            });
            await loadAttributeOptions(data);
            setDetail(data);
            setValues(initialValues);
            setStep(2);
        } catch (error) {
            console.error(error);
            showError("Unable to retrieve the VIN fragment. Please try again.");
        }
    };

    const loadAttributeOptions = async (data: VinFragmentDetail) => {
        const attributeIds = Array.from(
            new Set(
                Object.values(data.attributes)
                    .map((attribute) => attribute.attributeId)
                    .filter((attributeId) => attributeId > 0)
            )
        );
        if (attributeIds.length === 0) {
            setOptionsByAttributeId({});
            return;
        }
        const response = await VinImportService.getVehicleAttributeValues({attributeIds});
        const attributeValues: any[] = response.data?.attributeValues ?? [];
        const grouped: Record<number, AttributeValueOption[]> = {};
        attributeValues.forEach((value) => {
            (grouped[value.attributeId] ??= []).push({
                attributeValueId: value.attributeValueId,
                attributeValueDescription: value.attributeValueDescription,
            });
        });
        Object.values(grouped).forEach((options) =>
            options.sort((first, second) =>
                first.attributeValueDescription.localeCompare(second.attributeValueDescription)
            )
        );
        setOptionsByAttributeId(grouped);
    };

    const handleValueChange = (name: string, value: string) => {
        setValues((previous) => ({...previous, [name]: value}));
    };

    const handleBack = () => {
        setStep(1);
    };

    const showSuccess = (message: string) => {
        notificationRef.current?.successMessage(message);
    };

    const resolveAttributeCode = (attribute: VinAttribute, value: string): number => {
        const options = attribute.attributeId > 0 ? optionsByAttributeId[attribute.attributeId] ?? [] : [];
        const matched = options.find((option) => option.attributeValueDescription === value);
        if (matched) {
            return matched.attributeValueId;
        }
        return value === attribute.attributeValue ? attribute.attributeCode : 0;
    };

    const buildPayload = () => {
        const payload: Record<string, any> = {
            id: detail?.id,
            vinSigniPattrnMask: detail?.vinSigniPattrnMask,
        };
        attributeEntries.forEach(([name, attribute]) => {
            const field = ATTRIBUTE_FIELD_MAP[name];
            if (!field) {
                return;
            }
            const value = values[name] ?? "";
            if (attribute.attributeCode === 0) {
                // Free-text attribute: send only the text value, no id/code.
                payload[field] = null;
                payload[`${field}Value`] = value;
                return;
            }
            payload[field] = resolveAttributeCode(attribute, value);
            payload[`${field}Value`] = value;
        });
        return payload;
    };

    const handleSubmit = () => {
        if (!detail || !hasChanges) {
            return;
        }
        if (hasValidationErrors) {
            showError("Please fix the highlighted attribute values before submitting.");
            return;
        }
        setConfirmOpen(true);
    };

    const resetForm = () => {
        setStep(1);
        setVinFragment("");
        setDetail(null);
        setValues({});
        setOptionsByAttributeId({});
    };

    const handleConfirmSubmit = async () => {
        setConfirmOpen(false);
        if (!detail) {
            return;
        }
        try {
            const payload = buildPayload();
            await VinUpdateService.newVinFragmentUpdate(payload);
            resetForm();
            showSuccess("Vehicle attributes updated successfully.");
        } catch (error) {
            console.error(error);
            showError("Unable to update the VIN pattern. Please try again.");
        }
    };

    return (
        <div className="vin-update-component">
            <h2 className="fomo-type--title">Vin Update</h2>

            <div className="vin-update-stepper">
                <div className={`vin-update-step ${step === 1 ? "active" : "done"}`}>
                    <span className="dot">{step === 1 ? "1" : "\u2713"}</span>
                    <span className="label">Enter Details</span>
                </div>
                <span className={`vin-update-bar ${step === 2 ? "filled" : ""}`}/>
                <div className={`vin-update-step ${step === 2 ? "active" : ""}`}>
                    <span className="dot">2</span>
                    <span className="label">Attributes</span>
                </div>
            </div>

            {step === 1 && (
                <div className="vin-update-card">
                    <h3>Step 1 &middot; Enter VIN Fragment</h3>
                    <p className="sub">Provide the VIN fragment to continue.</p>
                    <div className="vin-update-field">
                        <input
                            id="vinFragment"
                            type="text"
                            placeholder=" "
                            maxLength={11}
                            value={vinFragment}
                            data-testid="vin-fragment"
                            onChange={handleVinChange}
                        />
                        <label htmlFor="vinFragment">VIN Pattern (e.g. 1FDYE1C8 TK)</label>
                    </div>
                    <p className="hint">Enter a VIN pattern like '1FDYE1C8 TK' &ndash; 8 characters, a space, then 2
                        characters, then select Next.</p>
                    <div className="modal-footer button-frame">
                        <button
                            data-testid="step-reset"
                            className="fvp-button--outline fvp-button"
                            type="button"
                            onClick={resetForm}
                        >
                            Reset
                        </button>
                        <div className="right-align">
                            <button
                                data-testid="step-next"
                                className="fvp-button button-right-align"
                                type="button"
                                onClick={handleNext}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && detail && (
                <div className="vin-update-card">
                    <h3>Step 2 &middot; Vehicle Attributes</h3>
                    <p className="sub">
                        Pattern <b>{detail.vinSigniPattrnMask}</b> (ID: {detail.id})
                    </p>

                    <div className="vin-update-grid">
                        {attributeEntries.map(([name, attribute]) => {
                            const isFreeText = attribute.attributeCode === 0;
                            const options = attribute.attributeId > 0
                                ? optionsByAttributeId[attribute.attributeId] ?? []
                                : [];
                            const currentValue = attribute.attributeValue;
                            const currentInOptions = currentValue === ""
                                || options.some((option) => option.attributeValueDescription === currentValue);
                            return (
                                <div className="vin-update-fld" key={name}>
                                    <span className="head">
                                        <span className="badge-ico">{ATTRIBUTE_ICONS[name] ?? "\uD83D\uDD16"}</span>
                                        <span className="tag">{ATTRIBUTE_LABELS[name] ?? name}</span>
                                    </span>
                                    {isFreeText ? (
                                        <input
                                            type="text"
                                            className={`vin-update-select vin-update-text${validationErrors[name] ? " vin-update-invalid" : ""}`}
                                            value={values[name] ?? ""}
                                            onChange={(event) => handleValueChange(name, event.target.value)}
                                        />
                                    ) : (
                                        <select
                                            className="vin-update-select"
                                            value={values[name] ?? ""}
                                            onChange={(event) => handleValueChange(name, event.target.value)}
                                        >
                                            <option value="">Select&hellip;</option>
                                            {!currentInOptions && (
                                                <option value={currentValue}>{currentValue}</option>
                                            )}
                                            {options.map((option) => (
                                                <option key={option.attributeValueId}
                                                        value={option.attributeValueDescription}>
                                                    {option.attributeValueDescription}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {validationErrors[name] && (
                                        <span className="vin-update-warning" role="alert">
                                            {validationErrors[name]}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="vin-update-progress">
                        <div className="info">
                            <b>{filledCount} / {totalCount}</b>
                            <span>attributes completed</span>
                        </div>
                        <div className="track">
                            <div className="fill" style={{width: `${percent}%`}}/>
                        </div>
                        <div className="pct">{percent}%</div>
                    </div>

                    <div className="modal-footer button-frame">
                        <button
                            data-testid="step-back"
                            className="fvp-button--outline fvp-button"
                            type="button"
                            onClick={handleBack}
                        >
                            <i className="fvp-left-chevron"/>Back
                        </button>
                        <button
                            data-testid="step-reset"
                            className="fvp-button--outline fvp-button"
                            type="button"
                            onClick={resetForm}
                        >
                            Reset
                        </button>
                        <div className="right-align">
                            <button
                                data-testid="step-submit"
                                className="fvp-button button-right-align"
                                type="button"
                                disabled={!hasChanges || hasValidationErrors}
                                onClick={handleSubmit}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CFAdminNotification ref={notificationRef}/>

            <CFAdminConfirmDialog
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmSubmit}
                title="Confirm Update"
                text="Are you sure you want to update the VIN pattern with these attributes?"
            />
        </div>
    );
}

export default CFAdminVinUpdate;
